import React, { useState, useEffect } from 'react';
import LandingPage from './components/LandingPage';
import SOCAnalyzer from './components/SOCAnalyzer';
import EngagementPlanning from './components/Planning/EngagementPlanning';
import DocumentIntakeWorkflow from './components/DocumentIntake/DocumentIntakeWorkflow';
import DocumentValidation from './components/DocumentIntake/DocumentValidation';
import GenericPdfExtractor from './components/DocumentIntake/GenericPdfExtractor';
import EngagementManager from './components/Engagement/EngagementManager';
import Login from './components/Login';
import JsonUrlViewer from './components/Tools/JsonUrlViewer';
import {
  isAuthenticated,
  getUserInfo,
  validateSession,
  startSessionRefresh,
  stopSessionRefresh,
  logout,
  persistAuthSession,
} from './utils/auth';
import './App.css';

const decodeMicrosoftUserInfo = (encodedUserInfo) => {
  const normalized = encodedUserInfo.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return JSON.parse(window.atob(padded));
};

function App() {
  const pathName = window.location.pathname || '';
  const isJsonViewerRoute = pathName === '/json-view' || pathName.startsWith('/json-view/');
  const isMicrosoftCallbackRoute = pathName === '/microsoft-callback';
  let jsonViewerRawTarget = '';
  if (isJsonViewerRoute && pathName.startsWith('/json-view/')) {
    try {
      jsonViewerRawTarget = decodeURIComponent(pathName.slice('/json-view/'.length));
    } catch (e) {
      jsonViewerRawTarget = pathName.slice('/json-view/'.length);
    }
  }
  if (isJsonViewerRoute && !jsonViewerRawTarget) {
    const params = new URLSearchParams(window.location.search || '');
    jsonViewerRawTarget = params.get('url') || '';
  }

  const [currentFeature, setCurrentFeature] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [sessionRefreshInterval, setSessionRefreshInterval] = useState(null);
  const [isValidatingSession, setIsValidatingSession] = useState(true);

  // Check authentication status on app load
  useEffect(() => {
    const checkAuth = async () => {
      setIsValidatingSession(true);

      if (isMicrosoftCallbackRoute) {
        const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
        const sessionToken = hashParams.get('session_token');
        const encodedUserInfo = hashParams.get('user_info');

        if (sessionToken && encodedUserInfo) {
          try {
            const parsedUserInfo = decodeMicrosoftUserInfo(encodedUserInfo);
            persistAuthSession(sessionToken, parsedUserInfo);
            setIsLoggedIn(true);
            setUserInfo(parsedUserInfo);
            setCurrentFeature(null);

            const intervalId = startSessionRefresh();
            setSessionRefreshInterval(intervalId);
            window.history.replaceState({}, document.title, '/');
            setIsValidatingSession(false);
            return;
          } catch (error) {
            console.error('Failed to complete Microsoft login callback:', error);
          }
        }

        window.location.replace('/?auth_error=Microsoft%20sign-in%20response%20was%20invalid');
        return;
      }
      
      if (isAuthenticated()) {
        // Validate session with backend
        const isValid = await validateSession();
        if (isValid) {
          setIsLoggedIn(true);
          setUserInfo(getUserInfo());
          // Start session refresh
          const intervalId = startSessionRefresh();
          setSessionRefreshInterval(intervalId);
        } else {
          // Session invalid, clear local storage
          setIsLoggedIn(false);
          setUserInfo(null);
        }
      } else {
        setIsLoggedIn(false);
        setUserInfo(null);
      }
      
      setIsValidatingSession(false);
    };

    checkAuth();

    // Cleanup on unmount
    return () => {
      if (sessionRefreshInterval) {
        stopSessionRefresh(sessionRefreshInterval);
      }
    };
  }, [isMicrosoftCallbackRoute]);

  const handleLoginSuccess = (user, token) => {
    setIsLoggedIn(true);
    setUserInfo(user);
    setCurrentFeature(null); // Reset to landing page
    
    // Start session refresh
    const intervalId = startSessionRefresh();
    setSessionRefreshInterval(intervalId);
  };

  const handleLogout = async () => {
    // Stop session refresh
    if (sessionRefreshInterval) {
      stopSessionRefresh(sessionRefreshInterval);
      setSessionRefreshInterval(null);
    }
    
    await logout();
    setIsLoggedIn(false);
    setUserInfo(null);
    setCurrentFeature(null);
  };

  const handleFeatureSelect = (featureId) => {
    const moduleMap = {
      'pdf-extractor': 'pdf_extractor',
      'engagement-manager': 'engagement_manager',
      'document-intake': 'document_intake',
      'soc-analyzer': 'soc_analyzer',
      'planning': 'planning',
    };
    const moduleAccess = userInfo?.module_access;
    const moduleKey = moduleMap[featureId];
    if (moduleKey && moduleAccess && typeof moduleAccess === 'object') {
      if (!moduleAccess['*'] && !moduleAccess[moduleKey]) {
        return;
      }
    }
    setCurrentFeature(featureId);
  };

  const handleBackToLanding = () => {
    setCurrentFeature(null);
  };

  const [selectedEngagement, setSelectedEngagement] = useState(null);

  const handleSelectEngagement = (engagement, featureOverride) => {
    setSelectedEngagement(engagement);
    setCurrentFeature(featureOverride || 'engagement-detail');
  };

  const renderCurrentFeature = () => {
    switch (currentFeature) {
      case 'soc-analyzer':
        return <SOCAnalyzer onBack={handleBackToLanding} userInfo={userInfo} />;
      case 'planning':
        return (
          <EngagementPlanning
            engagementId={selectedEngagement?.id || "demo_engagement"}
            onBack={() => setCurrentFeature('engagement-manager')}
            userInfo={userInfo}
          />
        );
      case 'document-intake':
        return (
          <DocumentIntakeWorkflow 
            auditorId={userInfo?.user_id || "demo_auditor"} 
            engagementId={selectedEngagement?.id || "demo_engagement"} 
            onBack={handleBackToLanding}
            userInfo={userInfo}
          />
        );
      case 'engagement-manager':
        return (
          <EngagementManager 
            userInfo={userInfo}
            onBack={handleBackToLanding}
            onSelectEngagement={handleSelectEngagement}
          />
        );
      case 'engagement-detail':
        return (
          <DocumentValidation 
            engagementId={selectedEngagement?.id || "demo_engagement"}
            userInfo={userInfo}
            onBack={() => setCurrentFeature('engagement-manager')}
            onMoveToPlanning={() => setCurrentFeature('planning')}
          />
        );
      case 'document-validation':
        return (
          <DocumentValidation 
            engagementId={selectedEngagement?.id || "demo_engagement"}
            userInfo={userInfo}
            onBack={handleBackToLanding}
          />
        );
      case 'pdf-extractor':
        return (
          <GenericPdfExtractor
            userInfo={userInfo}
            onBack={handleBackToLanding}
          />
        );
      default:
        return <LandingPage onFeatureSelect={handleFeatureSelect} userInfo={userInfo} onLogout={handleLogout} />;
    }
  };

  // Show loading screen while validating session
  if (isJsonViewerRoute) {
    return (
      <div className="App">
        <JsonUrlViewer initialUrl={jsonViewerRawTarget} />
      </div>
    );
  }

  if (isMicrosoftCallbackRoute) {
    return (
      <div className="App">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div>Loading...</div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Completing Microsoft sign-in...</div>
        </div>
      </div>
    );
  }

  // Show loading screen while validating session
  if (isValidatingSession) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column',
          gap: '1rem'
        }}>
          <div>Loading...</div>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>Validating session...</div>
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isLoggedIn) {
    return (
      <div className="App">
        <Login onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  // Show main app if authenticated
  return (
    <div className="App">
      {renderCurrentFeature()}
    </div>
  );
}

export default App;
